export const CHECKOUT_URL =
  'https://volleyballcoachsystem.impultienda.ar/checkout'

export const IMAGES = {
  bundle:
    'https://cdn.magicpatterns.com/uploads/87a3Az5fkvy96kDDb7cLD7/ChatGPT_Image_11_sept_2026,_19_59_34.png',
}

export type SamplePage = {
  src: string
  category: string
  title: string
  alt: string
}

/**
 * Real internal pages extracted from Volleyball Coach System™
 * (Volume 2 — The Volleyball Drill Library™ and
 *  Volume 4 — The Team Systems & Rotations Playbook™).
 *
 * MISSING ASSET: the "Live Serve Passing" page (Volume 2) was not supplied.
 * Do not generate a replacement — add the real PNG here when available:
 * {
 *   src: '<real Live Serve Passing PNG url>',
 *   category: 'Volume 2 — Drill Library',
 *   title: 'Live Serve Passing',
 *   alt: 'Volleyball Coach System page for the Live Serve Passing drill, with purpose, setup, court diagram and coaching cues.',
 * },
 */
export const samplePages: SamplePage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/ozQtH1XaR9oMUqX3UoSyUC/01_V2_Serve_to_Disrupt.png',
    category: 'Volume 2 — Drill Library',
    title: 'Serve to Disrupt',
    alt: 'Volleyball Coach System page for drill 012, Serve to Disrupt, with purpose, setup, a court diagram showing three passers and target zones, coaching cues and progressions.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/rGFDcZwU7WVUkbrqt7QMJm/04_Volume_4_Team_Systems_Rotations.png',
    category: 'Volume 4 — Team Systems & Rotations',
    title: '5–1 Setter Back Row',
    alt: 'Volleyball Coach System page showing the 5–1 rotation with the setter in the back row, all six player positions and the setter release path to the net.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/vrJSV6QrPzvefGGsJ9KHRA/03_V2_Set_to_Side_Out.png',
    category: 'Volume 2 — Drill Library',
    title: 'Set to Side-Out',
    alt: 'Volleyball Coach System page for drill 042, Set to Side-Out, with a court diagram showing passers, setter and hitters, plus coaching cues and ways to make it harder.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/beWqMbfdnFRUa34VbYGtbB/07_V2_Attack_vs_Defense.png',
    category: 'Volume 2 — Drill Library',
    title: 'Tip Attack & Roll Shot Attack',
    alt: 'Volleyball Coach System page for attacking drills 051 and 052, Tip Attack and Roll Shot Attack, each with court diagrams, setup, how it works and coaching cues.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ioS5Qk4bXr4KmPoVJD1NJz/05_V4_6-2_Offense.png',
    category: 'Volume 4 — Team Systems & Rotations',
    title: '6–2 Offense',
    alt: 'Volleyball Coach System page explaining the 6–2 offense with two setters and three front-row attackers, shown on a court diagram.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/nhppw54cwRLZcZFKxvZbWu/08_V2_Block_Defense_Connection.png',
    category: 'Volume 2 — Drill Library',
    title: 'Rally Challenge & Rotation Game',
    alt: 'Volleyball Coach System page for team play drills 071 and 072, Rally Challenge and Rotation Game, with court diagrams showing player positions, ball movement and coaching cues.',
  },
]

export type Stage = {
  label: string
  description: string
}

export const stages: Stage[] = [
  {
    label: 'Open',
    description:
      'Open the system and go straight to the resource your team needs today.',
  },
  {
    label: 'Choose',
    description:
      'Choose a drill, progression, team system or complete ready-to-run practice.',
  },
  {
    label: 'Coach',
    description:
      'Run the session with a clear plan, then adapt it to what your players need.',
  },
]

export type Volume = {
  number: number
  stage: string
  title: string
  benefit: string
}

export const volumes: Volume[] = [
  {
    number: 1,
    stage: 'Practice planning',
    title: 'The Volleyball Practice Planning System™',
    benefit:
      'How to structure, adapt and run better volleyball practices without rebuilding every session from scratch.',
  },
  {
    number: 2,
    stage: 'Drill library',
    title: 'The Volleyball Drill Library™',
    benefit:
      '100+ visual, ready-to-use volleyball drills organized to help you find what your team needs faster.',
  },
  {
    number: 3,
    stage: 'Skill development',
    title: 'The Skills Development System™',
    benefit:
      'Progressions, corrections and development pathways for building better volleyball players.',
  },
  {
    number: 4,
    stage: 'Team systems',
    title: 'The Team Systems & Rotations Playbook™',
    benefit:
      'Rotations, serve receive, offense, defense and team organization made clearer and easier to coach.',
  },
  {
    number: 5,
    stage: 'Ready to run',
    title: 'The Complete Practice Plans™',
    benefit:
      '60 complete volleyball practices you can open, follow, adapt and run.',
  },
  {
    number: 6,
    stage: 'Season planning',
    title: 'The Season Planning System™',
    benefit:
      'Build, organize and manage your volleyball season from preseason through championship play.',
  },
]

export type Bonus = {
  number: number
  title: string
  description: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'Volleyball Coach Communication Playbook™',
    description:
      'What to say, when to say it and how to build a more connected team.',
  },
  {
    number: 2,
    title: 'Volleyball Game Day Strategy Guide™',
    description:
      'Prepare, read the match and make smarter coaching adjustments.',
  },
  {
    number: 3,
    title: 'The Volleyball Coach’s Troubleshooting Guide™',
    description:
      '50 common volleyball team problems — and what to do next.',
  },
  {
    number: 4,
    title: 'Volleyball Team Culture & Leadership™',
    description:
      'Build standards, accountability, confidence and a stronger team environment.',
  },
  {
    number: 5,
    title: 'Volleyball Coaching Cues Handbook™',
    description:
      '100+ simple coaching cues for faster, clearer volleyball feedback.',
  },
]

export const builtToHelp: string[] = [
  'Spend less time planning practices from a blank page',
  'Keep drills, practices and coaching systems organized in one place',
  'Move from skill development to team systems with a clearer structure',
  'Run more consistent, purposeful practices throughout the season',
]

export const offerIncludes: string[] = [
  '6 Professional Coaching Volumes',
  '5 Premium Bonus Guides',
  '100+ Ready-to-Use Volleyball Drills',
  '60 Complete Practice Plans',
  'Skills Development + Team Systems & Rotations',
  'Complete Season Planning System',
  'OPEN → CHOOSE → COACH framework',
  'Instant Digital Access',
  'One-Time Payment',
]

export type Faq = {
  question: string
  answer: string
}

export const faqs: Faq[] = [
  {
    question: 'Is Volleyball Coach System™ a physical product?',
    answer:
      'No. Volleyball Coach System™ is a 100% digital coaching resource. You receive instant digital access after purchase.',
  },
  {
    question: 'Is this a course?',
    answer:
      'No. This is not a course or training program you need to complete. It is a ready-to-use professional coaching system designed to be opened whenever you need a drill, practice, progression, team system or planning resource.',
  },
  {
    question: 'Is this software or an app?',
    answer:
      'No. There is no software, app, subscription or dashboard. The system consists of professional digital coaching volumes and bonus guides.',
  },
  {
    question: 'Is it suitable for newer volleyball coaches?',
    answer:
      'Yes. The system provides structure, progressions, complete practices and clear coaching frameworks that can make planning much easier for newer coaches.',
  },
  {
    question: 'Can experienced volleyball coaches use it?',
    answer:
      'Yes. Experienced coaches can use the system as an organized resource library to save planning time, find drills quickly, adapt practices and support team organization throughout the season.',
  },
  {
    question: 'What volleyball levels is it designed for?',
    answer:
      'The system is designed to be useful across youth, middle school, high school and club volleyball. Coaches should adapt drills, practice intensity and tactical concepts to their athletes and competition level.',
  },
  {
    question: 'Are the 100+ drills videos?',
    answer:
      'No. The drills are presented as visual, written coaching resources with clear instructions, diagrams and coaching information. They are not a video library.',
  },
  {
    question: 'Are the 60 practices already written and ready to use?',
    answer:
      'Yes. Volume 5 includes 60 complete ready-to-run volleyball practices that can be opened, followed and adapted to your team.',
  },
  {
    question: 'Does it cover rotations and team systems?',
    answer:
      'Yes. Volume 4 focuses specifically on rotations, serve receive, offensive systems, defensive organization, transition and team structure.',
  },
  {
    question: 'Does this replace a coach’s judgment or organization rules?',
    answer:
      'No. Volleyball Coach System™ is a coaching resource. Always adapt the material to your athletes, league, organization, competition level and applicable rules.',
  },
  {
    question: 'How do I receive it?',
    answer:
      'You receive digital access after completing your purchase. No physical product will be shipped.',
  },
]
